import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  createReferringClinic,
  createReferringDoctor,
  deleteReferringDoctor,
  findReferringDoctorByDcr,
  listReferringClinics,
  resetReferringPortalPassword,
  updateReferringDoctor,
  type ReferringClinic,
  type ReferringDoctor,
  type ReferringDoctorWithStats,
} from '@/services/referringDirectoryService';

// Readable temporary password for a portal reset.
const makeTempPassword = (): string => {
  const a = ['Bright', 'Swift', 'Calm', 'Bold', 'Clear', 'Prime'];
  const n = ['Tooth', 'Molar', 'Smile', 'Crown', 'Root', 'Enamel'];
  const pick = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];
  return `${pick(a)}${pick(n)}${String(Math.floor(1000 + Math.random() * 9000))}`;
};

interface AddReferringDentistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog renders in edit mode against this row. */
  editingDoctor?: ReferringDoctorWithStats | ReferringDoctor | null;
  /** Optional callback after a successful save so the parent can refresh other state. */
  onSaved?: (doctor: ReferringDoctor) => void;
}

interface FormState {
  name: string;
  clinic_id: string; // '' means "no clinic"
  email: string;
  phone: string;
  specialty: string;
  dcr_number: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  clinic_id: '',
  email: '',
  phone: '',
  specialty: '',
  dcr_number: '',
  is_active: true,
};

// Singapore Dental Council registration: 5 digits + 1 letter (e.g. 31432I).
// Input is trimmed + uppercased before validation, so uppercase-only here.
const DCR_PATTERN = /^\d{5}[A-Z]$/;
const DCR_FORMAT_ERROR = 'DCR must be 5 digits followed by a letter, e.g. 31432I';

const NO_CLINIC_VALUE = '__none__';
const NEW_CLINIC_VALUE = '__new__';

interface NewClinicState {
  open: boolean;
  name: string;
  email: string;
  phone: string;
}

const EMPTY_NEW_CLINIC: NewClinicState = {
  open: false,
  name: '',
  email: '',
  phone: '',
};

function emailIsValid(value: string): boolean {
  if (value.trim() === '') return true;
  return /.+@.+\..+/.test(value.trim());
}

const AddReferringDentistDialog = ({
  open,
  onOpenChange,
  editingDoctor = null,
  onSaved,
}: AddReferringDentistDialogProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isEditing = !!editingDoctor;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [newClinic, setNewClinic] = useState<NewClinicState>(EMPTY_NEW_CLINIC);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const clinicsQuery = useQuery({
    queryKey: ['referring_clinics', 'active_for_picker'],
    queryFn: () => listReferringClinics({ activeOnly: true }),
    enabled: open,
  });

  // Hydrate form when the dialog opens or the editing target changes.
  useEffect(() => {
    if (!open) return;
    if (editingDoctor) {
      setForm({
        name: editingDoctor.name ?? '',
        clinic_id: editingDoctor.clinic_id ?? '',
        email: editingDoctor.email ?? '',
        phone: editingDoctor.phone ?? '',
        specialty: editingDoctor.specialty ?? '',
        dcr_number: (editingDoctor as any).dcr_number ?? '',
        is_active: editingDoctor.is_active ?? true,
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setNewClinic(EMPTY_NEW_CLINIC);
    setErrors({});
  }, [open, editingDoctor]);

  const createClinicMutation = useMutation({
    mutationFn: createReferringClinic,
    onSuccess: async (clinic) => {
      await queryClient.invalidateQueries({ queryKey: ['referring_clinics'] });
      setForm((prev) => ({ ...prev, clinic_id: clinic.id }));
      setNewClinic(EMPTY_NEW_CLINIC);
      toast({
        title: 'Clinic added',
        description: `${clinic.name} is now available in the clinic list.`,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not add clinic',
        description:
          error instanceof Error
            ? error.message
            : 'Unknown error creating clinic.',
        variant: 'destructive',
      });
    },
  });

  const saveDoctorMutation = useMutation({
    mutationFn: async (input: FormState): Promise<ReferringDoctor> => {
      const payload = {
        name: input.name.trim(),
        clinic_id: input.clinic_id ? input.clinic_id : null,
        email: input.email.trim() === '' ? null : input.email.trim(),
        phone: input.phone.trim() === '' ? null : input.phone.trim(),
        specialty: input.specialty.trim() === '' ? null : input.specialty.trim(),
        dcr_number: input.dcr_number.trim() === '' ? null : input.dcr_number.trim().toUpperCase(),
        is_active: input.is_active,
      };
      if (isEditing && editingDoctor) {
        return updateReferringDoctor(editingDoctor.id, payload);
      }
      return createReferringDoctor(payload);
    },
    onSuccess: async (doctor) => {
      await queryClient.invalidateQueries({ queryKey: ['referring_doctors'] });
      await queryClient.invalidateQueries({ queryKey: ['referring_clinics'] });
      toast({
        title: isEditing ? 'Doctor updated' : 'Doctor added',
        description: doctor.name,
      });
      onSaved?.(doctor);
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: isEditing ? 'Could not update doctor' : 'Could not add doctor',
        description:
          error instanceof Error
            ? error.message
            : 'Unknown error saving doctor.',
        variant: 'destructive',
      });
    },
  });

  // Portal account on the row being edited (present only for the stats variant).
  const portalAccount =
    (editingDoctor as ReferringDoctorWithStats | null)?.portal_account ?? null;

  const [resetOpen, setResetOpen] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const resetPasswordMutation = useMutation({
    mutationFn: () => {
      if (!portalAccount?.id) throw new Error('No portal account to reset.');
      return resetReferringPortalPassword(portalAccount.id, resetPassword);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['referring_doctors'] });
      toast({
        title: 'Password reset',
        description: `Share the new temporary password with ${editingDoctor?.name ?? 'the dentist'}.`,
      });
      setResetOpen(false);
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not reset password',
        description: error instanceof Error ? error.message : 'Unknown error.',
        variant: 'destructive',
      });
    },
  });

  const deleteDoctorMutation = useMutation({
    mutationFn: () => {
      if (!editingDoctor) throw new Error('No dentist to delete.');
      return deleteReferringDoctor(editingDoctor.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['referring_doctors'] });
      toast({
        title: 'Dentist deleted',
        description: editingDoctor?.name ?? '',
      });
      setDeleteOpen(false);
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not delete dentist',
        description: error instanceof Error ? error.message : 'Unknown error.',
        variant: 'destructive',
      });
    },
  });

  const [dcrChecking, setDcrChecking] = useState(false);

  const handleDcrChange = (rawValue: string) => {
    const value = rawValue.toUpperCase();
    setForm((prev) => ({ ...prev, dcr_number: value }));
    // Live inline feedback: clear or set the format error as the user types.
    setErrors((prev) => {
      const next = { ...prev };
      if (value.trim() !== '' && !DCR_PATTERN.test(value.trim())) {
        next.dcr_number = DCR_FORMAT_ERROR;
      } else {
        delete next.dcr_number;
      }
      return next;
    });
  };

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (form.name.trim() === '') {
      nextErrors.name = 'Doctor name is required.';
    }
    if (!emailIsValid(form.email)) {
      nextErrors.email = 'Enter a valid email address.';
    }
    if (form.dcr_number.trim() !== '' && !DCR_PATTERN.test(form.dcr_number.trim().toUpperCase())) {
      nextErrors.dcr_number = DCR_FORMAT_ERROR;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    // Duplicate-DCR guard: another dentist holding the same DCR blocks the save.
    // The DB unique index on upper(dcr_number) is the hard backstop; this check
    // exists to give a readable error instead of a constraint violation.
    const dcr = form.dcr_number.trim().toUpperCase();
    if (dcr !== '') {
      setDcrChecking(true);
      try {
        const existing = await findReferringDoctorByDcr(
          dcr,
          isEditing && editingDoctor ? editingDoctor.id : undefined,
        );
        if (existing) {
          setErrors((prev) => ({
            ...prev,
            dcr_number: `This DCR is already registered to ${existing.name}`,
          }));
          return;
        }
      } catch {
        // Lookup failed (network/RLS) — proceed; the unique index still blocks
        // a true duplicate and surfaces through the mutation's error toast.
      } finally {
        setDcrChecking(false);
      }
    }

    saveDoctorMutation.mutate(form);
  };

  const handleClinicChange = (value: string) => {
    if (value === NO_CLINIC_VALUE) {
      setForm((prev) => ({ ...prev, clinic_id: '' }));
      setNewClinic((prev) => ({ ...prev, open: false }));
      return;
    }
    if (value === NEW_CLINIC_VALUE) {
      setNewClinic((prev) => ({ ...prev, open: true }));
      return;
    }
    setForm((prev) => ({ ...prev, clinic_id: value }));
    setNewClinic((prev) => ({ ...prev, open: false }));
  };

  const handleCreateClinic = () => {
    if (newClinic.name.trim() === '') {
      toast({
        title: 'Clinic name required',
        description: 'Enter a name for the new clinic before saving.',
        variant: 'destructive',
      });
      return;
    }
    createClinicMutation.mutate({
      name: newClinic.name,
      email: newClinic.email,
      phone: newClinic.phone,
    });
  };

  const clinics: ReferringClinic[] = clinicsQuery.data ?? [];
  const selectedClinicValue = useMemo(() => {
    if (newClinic.open) return NEW_CLINIC_VALUE;
    if (form.clinic_id === '') return NO_CLINIC_VALUE;
    return form.clinic_id;
  }, [form.clinic_id, newClinic.open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit referring dentist' : 'Add referring dentist'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the contact details or clinic affiliation for this dentist.'
                : 'Add a new referring dentist to the directory. They can optionally be linked to a clinic.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="dentist-name">
                Doctor name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="dentist-name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Dr. Jane Tan"
                autoFocus
              />
              {errors.name ? (
                <p className="text-xs text-red-600">{errors.name}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dentist-dcr">DCR number</Label>
              <Input
                id="dentist-dcr"
                value={form.dcr_number}
                onChange={(e) => handleDcrChange(e.target.value)}
                placeholder="31432I"
                maxLength={6}
              />
              {errors.dcr_number ? (
                <p className="text-xs text-red-600">{errors.dcr_number}</p>
              ) : (
                <p className="text-xs text-slate-500">
                  Singapore Dental Council Registration — 5 digits + 1 letter
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dentist-clinic">Clinic</Label>
              <Select
                value={selectedClinicValue}
                onValueChange={handleClinicChange}
                disabled={clinicsQuery.isLoading}
              >
                <SelectTrigger id="dentist-clinic">
                  <SelectValue placeholder="Select a clinic (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CLINIC_VALUE}>No clinic</SelectItem>
                  {clinics.map((clinic) => (
                    <SelectItem key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={NEW_CLINIC_VALUE}>
                    + Create new clinic
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newClinic.open ? (
              <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">
                    New clinic details
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setNewClinic(EMPTY_NEW_CLINIC)}
                  >
                    Cancel
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-clinic-name" className="text-xs">
                    Clinic name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="new-clinic-name"
                    value={newClinic.name}
                    onChange={(e) =>
                      setNewClinic((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Smile Dental @ Tampines"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label htmlFor="new-clinic-email" className="text-xs">
                      Clinic email
                    </Label>
                    <Input
                      id="new-clinic-email"
                      type="email"
                      value={newClinic.email}
                      onChange={(e) =>
                        setNewClinic((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder="reception@..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-clinic-phone" className="text-xs">
                      Clinic phone
                    </Label>
                    <Input
                      id="new-clinic-phone"
                      value={newClinic.phone}
                      onChange={(e) =>
                        setNewClinic((prev) => ({ ...prev, phone: e.target.value }))
                      }
                      placeholder="6123 4567"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCreateClinic}
                  disabled={createClinicMutation.isPending}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {createClinicMutation.isPending ? 'Saving...' : 'Save clinic'}
                </Button>
              </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="dentist-email">Email</Label>
                <Input
                  id="dentist-email"
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="dr.tan@..."
                />
                {errors.email ? (
                  <p className="text-xs text-red-600">{errors.email}</p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dentist-phone">Phone</Label>
                <Input
                  id="dentist-phone"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  placeholder="9123 4567"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="dentist-specialty">Specialty / notes</Label>
              <Input
                id="dentist-specialty"
                value={form.specialty}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, specialty: e.target.value }))
                }
                placeholder="General dentistry, periodontics, etc."
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="dentist-active"
                checked={form.is_active}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, is_active: checked === true }))
                }
              />
              <Label
                htmlFor="dentist-active"
                className="text-sm font-normal cursor-pointer"
              >
                Active in directory
              </Label>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            {isEditing ? (
              <div className="flex flex-wrap gap-2">
                {portalAccount && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setResetPassword(makeTempPassword());
                      setResetOpen(true);
                    }}
                  >
                    <KeyRound className="mr-2 h-4 w-4" />
                    Reset password
                  </Button>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saveDoctorMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saveDoctorMutation.isPending || dcrChecking || !!errors.dcr_number}
              >
                {saveDoctorMutation.isPending || dcrChecking
                  ? 'Saving...'
                  : isEditing
                    ? 'Save changes'
                    : 'Add dentist'}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Reset portal password — warning + new temp password. */}
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset portal password?</AlertDialogTitle>
            <AlertDialogDescription>
              This sets a new temporary password for {editingDoctor?.name ?? 'this dentist'}
              {portalAccount?.email ? ` (${portalAccount.email})` : ''}. Their current
              password stops working immediately, and they'll be asked to set a new one at
              next login. Share this with them securely.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-center gap-2 py-1">
            <Input
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              aria-label="New temporary password"
            />
            <Button type="button" variant="outline" onClick={() => setResetPassword(makeTempPassword())}>
              Regenerate
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                resetPasswordMutation.mutate();
              }}
              disabled={resetPasswordMutation.isPending || resetPassword.trim().length < 4}
            >
              {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete dentist — destructive warning. */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {editingDoctor?.name ?? 'this dentist'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the dentist from the directory{portalAccount ? ' and deletes their portal login' : ''}.
              Past referrals are kept but will no longer be linked to this dentist. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteDoctorMutation.mutate();
              }}
              disabled={deleteDoctorMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteDoctorMutation.isPending ? 'Deleting...' : 'Delete dentist'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default AddReferringDentistDialog;
